import {
  Filter,
  useGetCountries,
  useGetFilterList,
  useGetNavigationalStatuses,
  useGetRegions,
  useGetShipTypes,
} from '@ais-ui/websockets';
import React, { SetStateAction, useEffect, useState } from 'react';
import {
  FilterCriteria,
  filterOptionsTemplate,
  HumanOperator,
  MappedShipDbToHumanOperators,
  MappedShipDbTypeToFrontendType,
} from './constants';
import {
  useFetchAllShiplists,
  useFlattenedGeometryShapes,
  useShipDbFilterTokens,
} from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { ShipList } from '@ais-ui/api-spec/config';
import { PiBoatThin } from 'react-icons/pi';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { useAuth } from '@ais-ui/auth';

const useShipDbFilterOptions = (
  setFilterOptions: React.Dispatch<
    SetStateAction<Record<string, FilterCriteria>>
  >
): boolean => {
  const permissions = usePermissions();

  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  const shipDbFilterTokensQuery = useShipDbFilterTokens(hasShipDbPermissions);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (shipDbFilterTokensQuery?.status === 'success') {
      for (const token of shipDbFilterTokensQuery.data) {
        const values: string[] = [];

        if (token.options) {
          values.push(...token.options);
        }

        const operators = token.operators
          .map((operator: string) => MappedShipDbToHumanOperators[operator])
          .filter(Boolean);

        const dataType = MappedShipDbTypeToFrontendType[token.type];
        const dataTypes: ('string' | 'number' | 'boolean')[] = [
          MappedShipDbTypeToFrontendType[token.type],
        ];

        // Add additional string operators if the dataType is string and has Equal operator
        if (dataType === 'string') {
          if (operators.includes(HumanOperator.Equal)) {
            // Add these positive string operators if they don't already exist
            const positiveStringOperators = [
              HumanOperator.StartsWith,
              HumanOperator.Contains,
              HumanOperator.EndsWith,
            ];

            // Add each operator if it doesn't already exist in the operators array
            positiveStringOperators.forEach((op) => {
              if (!operators.includes(op)) {
                operators.push(op);
              }
            });
          }

          if (operators.includes(HumanOperator.NotEqual)) {
            // Add these negative string operators if they don't already exist
            const negativeStringOperators = [
              HumanOperator.DoesNotStartWith,
              HumanOperator.NotContains,
              HumanOperator.DoesNotEndWith,
            ];

            // Add each operator if it doesn't already exist in the operators array
            negativeStringOperators.forEach((op) => {
              if (!operators.includes(op)) {
                operators.push(op);
              }
            });
          }
        }

        const name = token.name;

        setFilterOptions((prev) => ({
          ...prev,
          [name]: {
            data_types: dataTypes,
            limit: {},
            operators,
            values: values,
            icon: PiBoatThin,
          },
        }));

        setIsSuccess(true);
      }
    }
  }, [
    setFilterOptions,
    shipDbFilterTokensQuery.data,
    shipDbFilterTokensQuery.status,
  ]);

  return isSuccess;
};

export const useFilterOptions = (): {
  filterOptions: Record<string, FilterCriteria>;
  isSuccess: boolean;
} => {
  const countriesQuery = useGetCountries();
  const shipTypesQuery = useGetShipTypes();
  const navigationalStatusesQuery = useGetNavigationalStatuses();
  const regionsQuery = useGetRegions();
  const filterListQuery = useGetFilterList();
  const geoshapesListQuery = useFlattenedGeometryShapes();

  const { data: shiplists, status: shiplistStatus } = useFetchAllShiplists();

  const [filterOptions, setFilterOptions] = useState(filterOptionsTemplate);
  const isSuccess = useShipDbFilterOptions(setFilterOptions);

  useEffect(() => {
    if (shiplistStatus === 'success') {
      const shiplistNames = shiplists?.records?.map(
        (shiplist: ShipList) => shiplist.name
      );

      setFilterOptions((prev) => ({
        ...prev,
        SHIPLIST: {
          ...prev.SHIPLIST,
          values: shiplistNames || [],
        },
      }));
    }
  }, [shiplistStatus, shiplists]);

  useEffect(() => {
    if (countriesQuery.status === 'success') {
      setFilterOptions((prev) => ({
        ...prev,
        BS_COUNTRY: {
          ...prev.BS_COUNTRY,
          values: countriesQuery.data || [],
        },
        COUNTRY: {
          ...prev.COUNTRY,
          values: countriesQuery.data || [],
        },
      }));
    }
  }, [countriesQuery.status, countriesQuery.data]);

  useEffect(() => {
    if (shipTypesQuery.status === 'success') {
      setFilterOptions((prev) => ({
        ...prev,
        TYPE_OF_SHIP: {
          ...prev.TYPE_OF_SHIP,
          values: shipTypesQuery.data || [],
        },
      }));
    }
  }, [shipTypesQuery.status, shipTypesQuery.data]);

  useEffect(() => {
    if (navigationalStatusesQuery.status === 'success') {
      setFilterOptions((prev) => ({
        ...prev,
        NAV_STATUS: {
          ...prev.NAV_STATUS,
          values: navigationalStatusesQuery.data || [],
        },
      }));
    }
  }, [navigationalStatusesQuery.status, navigationalStatusesQuery.data]);

  useEffect(() => {
    if (regionsQuery.status === 'success') {
      setFilterOptions((prev) => ({
        ...prev,
        REGION: {
          ...prev.REGION,
          values: regionsQuery.data || [],
        },
      }));
    }
  }, [regionsQuery.status, regionsQuery.data]);

  useEffect(() => {
    if (filterListQuery.status === 'success') {
      setFilterOptions((prev) => ({
        ...prev,
        FILTER: {
          ...prev.FILTER,
          values:
            filterListQuery.data?.map((filter: Filter) => filter.name) || [],
        },
      }));
    }
  }, [filterListQuery.status, filterListQuery.data]);
  useEffect(() => {
    if (geoshapesListQuery.status === 'success') {
      setFilterOptions((prev) => ({
        ...prev,
        // Assume GEOSHAPES is the key for geoshapesList data
        POSITION: {
          ...prev.POSITION,
          values:
            geoshapesListQuery.data?.map(
              (shape: GeoJson) => shape.properties.name
            ) || [],
        },
      }));
    }
  }, [geoshapesListQuery.status, geoshapesListQuery.data]);

  return { filterOptions, isSuccess };
};
